import { useState } from 'react';
import { Link } from 'react-router-dom';

import { buttonVariants } from '@/components/ui/button';

import styles from './LandingPage.module.scss';
import { waitlistApi } from '../../api/waitlist';
import { Logo } from '../../components/Logo';
import { logError } from '../../lib/logger';

/**
 * Landing（E5-02-02）。根網址的第一眼：這是什麼、對我有什麼用、怎麼開始。
 *
 * Signature 是 hero 右側「活的對話」——顧客問、AI 答、轉真人、成交，
 * 逐條浮現一輪講完整個產品。文案寫給台灣中小電商的老闆：說人話、講具體、
 * 不吹（「答不了會轉真人」照實寫——那本來就是賣點）。
 *
 * 頁面順序照「客人自己的順序」走：先認出自己的痛（開四個後台）、
 * 再看有什麼（五塊功能）、再看怎麼運作（訊息進來之後發生什麼事）、
 * 再看為什麼要選這套（四個押注）、最後才是價錢與疑慮。
 *
 * 刻意不用 i18n：landing 是對台灣市場的單語銷售頁，跟後台的多語系是兩件事；
 * 定價數字寫死在文案（方案變動本來就該連文案一起改）。
 *
 * **封測期間主 CTA 是候補名單，不是註冊**（公開註冊由 `PUBLIC_REGISTRATION` 擋在後端）。
 * 定價照樣列出來，但上面明講「尚未開放購買」——列出來是為了讓人知道之後要收錢，
 * 不是為了讓人以為現在買得到。頁面上每一句話都要跟現實對得上。
 *
 * 通路狀態、功能清單、API 端點都照 README 的口徑寫——**沒做到的不寫**，
 * IG 就寫「等 Meta 審核」。誠實是這頁的語氣，也省掉之後被抓包的成本。
 */

/** 對話示範的腳本。delay 拉出「一來一往」的節奏；reduced-motion 時全部直接顯示。 */
const SCRIPT = [
  { kind: 'in', text: '請問這件外套 M 號還有貨嗎？', delay: 0.3 },
  { kind: 'out', text: 'M 號還有 3 件！尺寸偏合身，平常穿 M 的話直接選 M 就可以。', delay: 1.4 },
  { kind: 'tag', text: 'AI 自動回覆 · 引用「尺寸建議」知識庫', delay: 2.2 },
  { kind: 'in', text: '那我上次的訂單可以改地址嗎？', delay: 3.2 },
  { kind: 'tag', text: '訂單個案 → 已轉真人，指派給 小美', delay: 4.2, highlight: true },
  { kind: 'out', text: '幫您改好囉！週五前會到，出貨後傳追蹤連結給您 📦', delay: 5.2 },
] as const;

/** 通路與**真實**狀態——照 README 的表，沒接通的就寫沒接通。 */
const CHANNELS = [
  { name: 'LINE', status: '已上線', live: true },
  { name: '官網聊天室', status: '已上線', live: true },
  { name: 'Messenger', status: '已驗證收發', live: true },
  { name: 'Instagram', status: '等 Meta 審核', live: false },
] as const;

/** 痛點三連。左邊是現在的日子，右邊是換掉之後——一對一對著寫，客人自己對號入座。 */
const PROBLEMS = [
  {
    now: '訊息散在四個 App',
    nowBody:
      'LINE 官方帳號一個後台、粉專小盒子一個、IG 一個。客服開四個分頁輪流看，誰接了、回多快、幾通整天沒人回，沒有人說得出數字。',
    next: '四個通路進同一個匣子。誰接手、平均多久回、幾通還沒回，報表一頁就看得到。',
  },
  {
    now: '行銷發完就斷線',
    nowBody:
      '廣播發出去，開了沒、點了沒、買了沒，全部靠猜。下一波要發什麼、發給誰，只能憑感覺再賭一次。',
    next: '每一則發出去的訊息都帶自己的追蹤連結，點擊與訂單對得回這一波、這一個人、這一步。',
  },
  {
    now: '線上線下是兩本帳',
    nowBody:
      '店裡結帳的客人和線上聊天的客人，系統認為是兩個人。門市導購幫忙賣掉的，也算不到他頭上。',
    next: '匿名訪客、通路好友、電商會員縫成同一份檔案；門市掃碼綁定，成交算得到導購的業績。',
  },
] as const;

/** 產品全景＝後台側欄的五組。寫「現在點得開的畫面」，不寫 roadmap。 */
const FEATURE_GROUPS = [
  {
    title: '日常',
    items: [
      ['首頁工作台', '「需要你接手」的對話直接列在這，旁邊是最近 7 天的數字'],
      ['全通路收件匣', '四通路合一，一萬筆對話捲動不卡，新訊息即時進來不用重整'],
      ['指派與協作', '指派、標籤、結案、內部備註——內部討論不會誤發給客人'],
      ['導購模式', '門市店員的簡化介面：只看指派給自己的對話，逾時會提醒'],
    ],
  },
  {
    title: '行銷',
    items: [
      ['分眾廣播', '排程發送，送達／點擊／轉換三個數字追到底'],
      ['分眾條件', '行為、屬性、訂單都能當條件，組合出一群人再發'],
      ['優惠券與抽獎輪盤', '發券、核銷、抽獎活動，在 LINE 裡直接玩'],
      ['自動化旅程', '加好友 → 隔天推首購券 → 沒買再推一次，設一次跑一整年'],
      ['罐頭訊息', '客服常用回覆存起來，兩個字叫出整段話'],
    ],
  },
  {
    title: '自動化',
    items: [
      ['關鍵字自動回覆', '規則引擎，前後端共用同一份比對邏輯——預覽看到的就是實際跑的'],
      ['視覺化聊天流程', '拖出一條 flow 就能跑，附現成範本直接改'],
      ['圖文選單', 'LINE rich menu，還能依分眾給不同人不同選單'],
      ['留言自動私訊', '粉專與 IG 貼文底下留言，自動轉成一對一私訊'],
    ],
  },
  {
    title: 'AI',
    items: [
      ['AI 輔助', '建議回覆、對話摘要、自動貼標、翻譯——真人回得更快'],
      ['知識庫', 'FAQ 餵進去，一條一個主題，答錯找得到是哪一條'],
      ['AI 客服 Agent', '自動回答常見問題，沒把握就轉真人並附上摘要'],
    ],
  },
  {
    title: '資料與整合',
    items: [
      ['報表', '對話量、回覆速度、CSAT、客服績效、轉換歸因'],
      ['電商整合', 'Shopify 訂單事件、出貨通知與成交歸因'],
      ['Open API 與 webhook', '聯絡人、對話、訊息、分眾都有 v1 API，附 OpenAPI 文件'],
      ['權限與安全', '通路憑證加密存放，成員分角色，跨租戶的資料互相讀不到'],
    ],
  },
] as const;

/** 「訊息進來之後發生什麼事」——把架構講成客人聽得懂的四步，但不騙。 */
const PIPELINE = [
  {
    no: '01',
    title: '先收下，再處理',
    body: '訊息進來的第一秒就收下並轉成同一種格式，處理排在後面做。平台對回應時間有秒級的限制，這樣安排才不會因為你這邊忙就漏訊息。',
  },
  {
    no: '02',
    title: '你設的規則先跑',
    body: '關鍵字與聊天流程永遠排在 AI 前面。商家自己設計的東西贏過機率模型——這是寫死的順序，不是設定裡的一個選項。',
  },
  {
    no: '03',
    title: 'AI 接最後一棒',
    body: '前面都沒接住才輪到 AI，查知識庫作答；沒把握的不硬掰，自動轉真人並附上對話摘要，顧客不用重講一次。',
  },
  {
    no: '04',
    title: '每一步都留痕',
    body: '訊息與狀態變化都寫進一張只增不改的事件表。報表、旅程、歸因全部從同一份事實長出來，所以兩個頁面的數字不會互相打架。',
  },
] as const;

/** 四個押注：不是功能清單，是「為什麼這套跟再多開一個後台不一樣」。 */
const EDGES = [
  {
    title: '歸因不是事後外掛的',
    body: '追蹤參數是在客人點下連結、轉址的那一刻才附加的。所以你改了規則，之前發出去的舊連結立刻跟著生效；而且不會蓋掉你自己帶的 UTM。追蹤壞掉也不會擋住訊息送出——客人該收到的東西永遠先送。',
  },
  {
    title: 'AI 只在該它上場時上場',
    body: 'AI 接在自動回應鏈的最後一棒，你寫的規則永遠先跑。知識庫是條目式的，一條就是一個主題，所以答錯永遠對得回某一條、當場修得掉，不是「再訓練看看」。',
  },
  {
    title: '同一個人只有一份檔案',
    body: '官網匿名訪客、通路好友、電商會員，在資料上是同一個人。官網行為蒐集併在聊天室那一行 script 裡——你本來就要貼的那一行就是全部，不用再貼第二段追蹤碼。預設關閉，關閉時前端根本不掛監聽。',
  },
  {
    title: '額度用完，擋的是行銷不是客服',
    body: '超量的時候暫停的是廣播與自動化旅程，收件匣的人工回覆永遠送得出去。付不出錢不該變成你的客人叫不到人——這是刻意寫進去的口徑，不是還沒做到的例外。',
  },
] as const;

/** 開始三步。每一步都寫「你實際要做的動作」，不寫「輕鬆上手」這種空話。 */
const STEPS = [
  {
    no: '1',
    title: '接上通路',
    body: '把 LINE 官方帳號的憑證貼進來，或把一行 script 放進官網。不用換帳號、不用搬好友。',
  },
  {
    no: '2',
    title: '把常見問題餵進去',
    body: 'FAQ 貼進知識庫、設幾條關鍵字、挑一個流程範本改成自己的。半天就有一個會回話的帳號。',
  },
  {
    no: '3',
    title: '開始賣',
    body: '發第一波分眾廣播、印門市導購的 QR code。從這一刻起每一則訊息帶回幾張訂單都算得出來。',
  },
] as const;

/** 常見問題。挑的是「會擋住成交的六個疑慮」，其中兩個的老實答案是「還沒有」。 */
const FAQ = [
  {
    q: '現在可以付費訂閱嗎？',
    a: '還不行。目前是邀請制封測，開放進來的人一律免費使用，付費方案還沒開賣。開賣前會先寄信說明價格與時間，不會突然開始收錢。',
  },
  {
    q: '要換掉現在的 LINE 官方帳號嗎？',
    a: '不用。用你現有的官方帳號接上來就好，好友一個都不會掉，舊的後台也還在。',
  },
  {
    q: 'Instagram 現在能用嗎？',
    a: '還不行。程式碼寫完了，卡在 Meta 的商業驗證與 App Review。LINE、Messenger 與官網聊天室現在就能用，IG 通過後直接開。',
  },
  {
    q: 'AI 會不會亂回答客人？',
    a: '沒把握的它不會硬答，會轉真人並附上摘要。而且你設計的流程與關鍵字永遠排在 AI 前面——不想讓 AI 碰的題目，設一條規則就攔下來了。',
  },
  {
    q: '客人的資料存在哪、安全嗎？',
    a: '資料庫在台灣時區的雲端服務上，通路憑證加密存放。每一次查詢都強制帶工作區，不同商家的資料在程式層就互相讀不到；成員還能分角色給不同權限。',
  },
  {
    q: '可以串我自己的系統嗎？',
    a: '可以。聯絡人、對話、訊息、分眾都有 v1 API，另外有 outbound webhook 把事件推給你。電商目前串 Shopify，SHOPLINE 還在等合作申請。',
  },
  {
    q: '超過訊息額度會怎樣？',
    a: '暫停的只有行銷發送（廣播、自動化旅程），客服回覆照常。你可以當月升級，也可以等下個月額度重置。',
  },
] as const;

/**
 * 候補名單表單。hero 與頁尾各放一份——兩個位置是同一個動作，不需要兩種說法。
 *
 * 封測期間**沒有公開註冊**，所以 landing 的主 CTA 是留 email 而不是「免費開始」：
 * 頁面上的每一句話都要跟現實對得上，包括按鈕。
 */
function WaitlistForm({ autoFocusId }: { autoFocusId: string }) {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (state === 'sending') return;

    setState('sending');
    try {
      await waitlistApi.join(email, 'landing');
      setState('done');
    } catch (error) {
      logError('候補名單送出失敗', error);
      setState('error');
    }
  }

  if (state === 'done') {
    return (
      <p className={styles.waitlistDone}>
        收到了，我們會依序邀請。開放的時候寄信給 <b>{email}</b>。
      </p>
    );
  }

  return (
    <form className={styles.waitlist} onSubmit={submit}>
      <div className={styles.waitlistRow}>
        <input
          id={autoFocusId}
          className={styles.waitlistInput}
          type="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="你的 email"
          aria-label="你的 email"
        />
        <button
          type="submit"
          className={buttonVariants({ size: 'lg' })}
          disabled={state === 'sending'}
        >
          {state === 'sending' ? '送出中⋯⋯' : '加入候補'}
        </button>
      </div>
      {state === 'error' && (
        <p className={styles.waitlistError} role="alert">
          送不出去，請稍後再試一次。
        </p>
      )}
    </form>
  );
}

export function LandingPage() {
  return (
    <div className={styles.page}>
      <div className={styles.inner}>
        <nav className={styles.nav}>
          <Logo size={30} />
          <div className={styles.navLinks}>
            <a href="#features">功能</a>
            <a href="#how">運作方式</a>
            <a href="#pricing">方案</a>
            <a href="#faq">常見問題</a>
          </div>
          <div className={styles.navActions}>
            <Link to="/login" className={buttonVariants({ variant: 'ghost', size: 'sm' })}>
              登入
            </Link>
            <a href="#waitlist" className={buttonVariants({ size: 'sm' })}>
              加入候補
            </a>
          </div>
        </nav>

        {/*
          main 地標：螢幕閱讀器靠它一鍵跳到內文（Lighthouse `landmark-one-main`，E6-08）。
          hero 的 <header> 包在 main 裡面才不會被當成整站的 banner——那個角色是上面的 nav。
        */}
        <main>
          <header className={styles.hero}>
            <div>
              <p className={styles.eyebrow}>
                全通路對話式商務平台 <span className={styles.betaTag}>邀請制封測中</span>
              </p>
              <h1 className={styles.heroTitle}>
                客人在哪裡聊，
                <br />
                生意就在<em>哪裡成交</em>
              </h1>
              <p className={styles.heroSub}>
                LINE、Messenger、Instagram、官網聊天——一個收件匣全部接住。 自動回覆、分眾廣播、AI
                客服，答不了的自動轉真人； 每一則發出去的訊息，都算得出帶回幾張訂單。
              </p>
              <div className={styles.ctaRow}>
                <WaitlistForm autoFocusId="waitlist-hero" />
                <a href="#pricing" className={buttonVariants({ variant: 'outline', size: 'lg' })}>
                  看方案
                </a>
              </div>
              <p className={styles.ctaNote}>
                封測期間免費使用，不用信用卡。付費方案還沒開放，開賣前會先通知你。
              </p>
            </div>

            {/* 活的對話：一輪講完「AI 答得了就答、答不了轉真人」 */}
            <div className={styles.phone} aria-label="對話示範">
              <div className={styles.phoneHeader}>
                <span className={styles.dotOnline} />
                好衣屋 官方帳號
              </div>
              <div className={styles.chat}>
                {SCRIPT.map((message, index) =>
                  message.kind === 'tag' ? (
                    <span
                      key={index}
                      className={styles.msgTag}
                      style={{ animationDelay: `${message.delay}s` }}
                    >
                      {'highlight' in message && message.highlight ? (
                        <>
                          <span className={styles.msgDot} aria-hidden="true" />
                          {message.text}
                        </>
                      ) : (
                        message.text
                      )}
                    </span>
                  ) : (
                    <span
                      key={index}
                      className={message.kind === 'in' ? styles.msgIn : styles.msgOut}
                      style={{ animationDelay: `${message.delay}s` }}
                    >
                      {message.text}
                    </span>
                  ),
                )}
              </div>
            </div>
          </header>

          {/* 通路狀態帶：把「哪些真的能用」放在第一眼看得到的地方 */}
          <section className={styles.channels} aria-label="支援通路">
            {CHANNELS.map((channel) => (
              <div key={channel.name} className={styles.channel}>
                <span className={channel.live ? styles.channelDot : styles.channelDotWaiting} />
                <b>{channel.name}</b>
                <small>{channel.status}</small>
              </div>
            ))}
          </section>

          <section className={styles.problems}>
            <h2 className={styles.sectionTitle}>開四個後台，還是沒有人說得出數字</h2>
            <p className={styles.sectionLead}>
              不是少一個工具，是三件事連不起來——訊息、行銷成效、線上線下的客人。
            </p>
            <div className={styles.problemGrid}>
              {PROBLEMS.map((problem) => (
                <div key={problem.now} className={styles.problem}>
                  <h3 className={styles.problemTitle}>{problem.now}</h3>
                  <p className={styles.problemBody}>{problem.nowBody}</p>
                  <p className={styles.problemNext}>{problem.next}</p>
                </div>
              ))}
            </div>
          </section>

          <section id="features" className={styles.features}>
            <h2 className={styles.sectionTitle}>一套後台，五塊功能</h2>
            <p className={styles.sectionLead}>
              側欄的五組就是產品的五塊。以下全是現在點得開的畫面，不是路線圖。
            </p>
            <div className={styles.featureGrid}>
              {FEATURE_GROUPS.map((group) => (
                <div key={group.title} className={styles.featureGroup}>
                  <h3 className={styles.featureGroupTitle}>{group.title}</h3>
                  <ul className={styles.featureList}>
                    {group.items.map(([name, desc]) => (
                      <li key={name}>
                        <b>{name}</b>
                        <span>{desc}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          <section id="how" className={styles.pipeline}>
            <h2 className={styles.sectionTitle}>訊息進來之後，發生什麼事</h2>
            <p className={styles.sectionLead}>
              四步，順序是固定的——知道順序，你才知道哪一步該由你接手。
            </p>
            <ol className={styles.pipelineList}>
              {PIPELINE.map((step) => (
                <li key={step.no} className={styles.pipelineStep}>
                  <span className={styles.pipelineNo}>{step.no}</span>
                  <h3 className={styles.pipelineTitle}>{step.title}</h3>
                  <p className={styles.pipelineBody}>{step.body}</p>
                </li>
              ))}
            </ol>
          </section>

          <section className={styles.omo}>
            <Logo size={56} withWordmark={false} />
            <div>
              <h2 className={styles.omoTitle}>線上線下，同一個客人</h2>
              <p className={styles.omoBody}>
                門市導購一人一組 QR code：客人掃了就是他的客人——之後的訊息路由給他、
                成交的訂單算他的業績。線上聊的、店裡買的，終於是同一本帳。
              </p>
              <ul className={styles.omoList}>
                <li>掃到誰就是誰的客人：對話當場指派，之後的訊息也路由給他</li>
                <li>業績在訂單成立當下就記下來、不回溯：客人之後換綁，舊單不動</li>
                <li>換綁算最後一次掃的——客人換店是真實情境，不是資料錯誤</li>
              </ul>
            </div>
          </section>

          <section className={styles.edges}>
            <h2 className={styles.sectionTitle}>為什麼不是再多開一個後台</h2>
            <p className={styles.sectionLead}>
              四個從第一天就決定要做對的地方。都是那種事後補不上、只能一開始就選邊站的事。
            </p>
            <div className={styles.edgeGrid}>
              {EDGES.map((edge) => (
                <div key={edge.title} className={styles.edge}>
                  <h3 className={styles.edgeTitle}>{edge.title}</h3>
                  <p className={styles.edgeBody}>{edge.body}</p>
                </div>
              ))}
            </div>
          </section>

          <section className={styles.steps}>
            <h2 className={styles.sectionTitle}>開始只要三件事</h2>
            <div className={styles.stepGrid}>
              {STEPS.map((step) => (
                <div key={step.no} className={styles.step}>
                  <span className={styles.stepNo}>{step.no}</span>
                  <h3 className={styles.stepTitle}>{step.title}</h3>
                  <p className={styles.stepBody}>{step.body}</p>
                </div>
              ))}
            </div>
          </section>

          <section id="pricing" className={styles.pricing}>
            <h2 className={styles.sectionTitle}>方案</h2>
            <p className={styles.sectionLead}>
              差別只在量：訊息則數、成員、通路與 AI 用量。功能沒有鎖在高階方案裡。
            </p>
            <p className={styles.pricingStatus}>
              <b>目前為邀請制封測，付費方案尚未開放購買。</b>
              下面是開賣後的定價——先讓你知道之後會長什麼樣，封測期間一律免費。
            </p>
            <div className={styles.plans}>
              <div className={styles.plan}>
                <p className={styles.planName}>Free</p>
                <p className={styles.planPrice}>
                  $0 <small>／月</small>
                </p>
                <p className={styles.planFor}>先接一條通路，把流程跑順</p>
                <ul className={styles.planList}>
                  <li>每月 500 則訊息</li>
                  <li>2 位成員、2 條通路</li>
                  <li>AI 每月 5 萬 tokens</li>
                </ul>
              </div>
              <div className={styles.planHot}>
                <span className={styles.planBadge}>最多人選</span>
                <p className={styles.planName}>Starter</p>
                <p className={styles.planPrice}>
                  NT$1,990 <small>／月</small>
                </p>
                <p className={styles.planFor}>一組客服團隊，每週發廣播</p>
                <ul className={styles.planList}>
                  <li>每月 5,000 則訊息</li>
                  <li>5 位成員、5 條通路</li>
                  <li>AI 每月 50 萬 tokens</li>
                </ul>
              </div>
              <div className={styles.plan}>
                <p className={styles.planName}>Pro</p>
                <p className={styles.planPrice}>
                  NT$4,990 <small>／月</small>
                </p>
                <p className={styles.planFor}>多門市、跑自動化旅程與歸因</p>
                <ul className={styles.planList}>
                  <li>每月 30,000 則訊息</li>
                  <li>15 位成員、10 條通路</li>
                  <li>AI 每月 200 萬 tokens</li>
                </ul>
              </div>
            </div>
            <div className={styles.included}>
              <p className={styles.includedTitle}>三個方案都含</p>
              <p className={styles.includedBody}>
                全通路收件匣 · 關鍵字自動回覆 · 視覺化聊天流程 · 分眾廣播 · 優惠券與輪盤 ·
                自動化旅程 · 圖文選單 · AI 輔助與知識庫 · 報表與轉換歸因 · 門市導購 · Open API 與
                webhook
              </p>
            </div>
            <p className={styles.pricingNote}>
              超過額度時只暫停行銷發送——收件匣的客服回覆永遠照常。先用免費的，長大再升級。
            </p>
          </section>

          <section id="faq" className={styles.faq}>
            <h2 className={styles.sectionTitle}>常見問題</h2>
            <div className={styles.faqGrid}>
              {FAQ.map((item) => (
                <div key={item.q} className={styles.faqItem}>
                  <h3 className={styles.faqQuestion}>{item.q}</h3>
                  <p className={styles.faqAnswer}>{item.a}</p>
                </div>
              ))}
            </div>
          </section>

          <section id="waitlist" className={styles.finalCta}>
            <h2 className={styles.finalTitle}>你的客人已經在 LINE 上等回覆了</h2>
            <p className={styles.finalBody}>
              現在是邀請制封測，一批一批開放。留個 email，輪到你的時候我們寄邀請給你——
              封測期間免費，不用信用卡。
            </p>
            <div className={styles.finalForm}>
              <WaitlistForm autoFocusId="waitlist-footer" />
            </div>
          </section>
        </main>

        <footer className={styles.footer}>
          <div className={styles.footerTop}>
            <div className={styles.footerBrand}>
              <Logo size={26} />
              <p>用聊天做生意。客人在哪裡聊，生意就在哪裡成交。</p>
            </div>
            <div className={styles.footerCol}>
              <p className={styles.footerColTitle}>產品</p>
              <a href="#features">功能</a>
              <a href="#how">運作方式</a>
              <a href="#pricing">方案</a>
              <a href="#faq">常見問題</a>
            </div>
            <div className={styles.footerCol}>
              <p className={styles.footerColTitle}>開發者</p>
              <a href="https://api.talkomo.com/docs" target="_blank" rel="noreferrer">
                API 文件
              </a>
              <a href="https://api.talkomo.com/healthz" target="_blank" rel="noreferrer">
                服務狀態
              </a>
            </div>
            <div className={styles.footerCol}>
              <p className={styles.footerColTitle}>法務</p>
              <Link to="/privacy">隱私權政策</Link>
              <Link to="/terms">服務條款</Link>
            </div>
          </div>
          <div className={styles.footerBottom}>
            <span>© {new Date().getFullYear()} Talkomo</span>
            <span>台灣製造 · 為台灣的中小電商而寫</span>
          </div>
        </footer>
      </div>
    </div>
  );
}
